import React from 'react';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import {
  Star,
  Check,
  X,
  ArrowRight,
  Clock,
  Shield,
  Gift,
  TrendingUp,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, Cell } from 'recharts';

const Comparison: React.FC = () => {
  const offers = [
    {
      id: 'bwin',
      name: 'bwin',
      image: '/images/offers/bwin.webp',
      rating: 4.8,
      bonus: '100% jusqu\'à 100€',
      minDeposit: '€10',
      wagering: '35x',
      payout: '24-48h',
      games: 500,
      license: 'Licencié',
      link: '/offres/bwin',
      pros: [
        'Bonus généreux',
        'Retrait rapide',
        'Beaucoup de sports',
        'Interface moderne',
      ],
      cons: ['Quelques restrictions de bonus'],
    },
    {
      id: 'netbet',
      name: 'NetBet',
      image: '/images/offers/netbet.webp',
      rating: 4.6,
      bonus: '150% jusqu\'à 150€',
      minDeposit: '€10',
      wagering: '40x',
      payout: '24-48h',
      games: 400,
      license: 'Licencié',
      link: '/offres/netbet',
      pros: [
        'Bonus équilibré',
        'Retrait rapide',
        'Interface intuitive',
      ],
      cons: ['Wagering plus élevé', 'Moins de sports'],
    },
    {
      id: 'genybet',
      name: 'Genybet',
      image: '/images/offers/genybet.webp',
      rating: 4.7,
      bonus: '200% jusqu\'à 200€',
      minDeposit: '€20',
      wagering: '30x',
      payout: '48-72h',
      games: 450,
      license: 'Licencié',
      link: '/offres/genybet',
      pros: [
        'Bonus très élevé',
        'Bonne variété',
        'Support en français',
      ],
      cons: ['Retrait plus lent', 'Dépôt minimum plus élevé'],
    },
  ];

  const comparisonData = offers.map((offer) => ({
    name: offer.name,
    rating: offer.rating,
    games: offer.games,
    bonus: parseFloat(offer.bonus.match(/\d+/)?.[0] || '0'),
  }));

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899'];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Comparaison des Offres de Bookmakers',
    description:
      'Comparez les meilleures offres de sites de paris sportifs en France. Voyez les bonus, les notes, les délais de paiement et bien plus encore.',
    url: 'https://primorixx.com/comparaison',
  };

  return (
    <Layout
      title="Comparaison des Offres"
      description="Comparez les meilleures offres de sites de paris sportifs en France. Voyez les bonus, les notes, les délais de paiement et les caractéristiques détaillées."
      canonicalUrl="/comparaison"
      jsonLd={jsonLd}
    >
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
                Comparaison des Offres
              </h1>
              <p className="text-xl text-dark-muted">
                Comparez les principaux bookmakers et trouvez l'offre parfaite pour
                vous
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="overflow-x-auto">
              <table className="w-full bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                <thead className="bg-dark-surface">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Bookmaker
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Note
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Bonus
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Dép. Min.
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Wagering
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Retrait
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Sports
                    </th>
                    <th className="px-6 py-4 text-left text-white font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer, index) => (
                    <tr
                      key={offer.id}
                      className={`border-t border-dark-border hover:bg-dark-surface/50 transition-colors ${
                        index === 0 ? 'bg-dark-surface/30' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <img
                            src={offer.image}
                            alt={offer.name}
                            className="w-16 h-16 object-contain"
                            loading="lazy"
                          />
                          <div>
                            <div className="font-semibold text-white">
                              {offer.name}
                            </div>
                            {index === 0 && (
                              <span className="text-xs text-yellow-400">
                                #1 Recommandé
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-white font-semibold">
                            {offer.rating}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-dark-accent font-semibold">
                          {offer.bonus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-dark-muted">
                        {offer.minDeposit}
                      </td>
                      <td className="px-6 py-4 text-dark-muted">
                        {offer.wagering}
                      </td>
                      <td className="px-6 py-4 text-dark-muted">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {offer.payout}
                      </td>
                      <td className="px-6 py-4 text-dark-muted">
                        {offer.games}+
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={offer.link}
                          className="inline-flex items-center text-dark-accent hover:text-dark-accentHover font-medium"
                        >
                          Voir les Détails
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Visual Comparison */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Comparaison Visuelle
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Note par Bookmaker
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData}>
                    <XAxis dataKey="name" stroke="#a0a0a0" />
                    <YAxis stroke="#a0a0a0" domain={[0, 5]} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="rating" radius={[8, 8, 0, 0]}>
                      {comparisonData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Nombre de Sports
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData}>
                    <XAxis dataKey="name" stroke="#a0a0a0" />
                    <YAxis stroke="#a0a0a0" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="games" radius={[8, 8, 0, 0]}>
                      {comparisonData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Comparison */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Analyse Détaillée
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-dark-card border border-dark-border rounded-xl p-6"
                >
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-32 object-contain mb-4"
                    loading="lazy"
                  />
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {offer.name}
                  </h3>

                  <div className="mb-4">
                    <h4 className="text-white font-semibold mb-2 flex items-center">
                      <Check className="w-5 h-5 text-green-400 mr-2" />
                      Avantages
                    </h4>
                    <ul className="space-y-1">
                      {offer.pros.map((pro, index) => (
                        <li
                          key={index}
                          className="text-dark-muted text-sm flex items-start"
                        >
                          <span className="mr-2">•</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-white font-semibold mb-2 flex items-center">
                      <X className="w-5 h-5 text-red-400 mr-2" />
                      Inconvénients
                    </h4>
                    <ul className="space-y-1">
                      {offer.cons.map((con, index) => (
                        <li
                          key={index}
                          className="text-dark-muted text-sm flex items-start"
                        >
                          <span className="mr-2">•</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={offer.link}
                    className="block w-full text-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors mt-4"
                  >
                    Voir l'Analyse Complète
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Comparison;

